import React, { useState, useEffect, useMemo } from "react";
import { useExpanded, usePagination, useSortBy, useTable } from "react-table";
import { QueryClient, QueryClientProvider, useQuery } from 'react-query'
import { JOURNAL_DETAILED_COLUMNS } from "./columns";
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination';
import { Col, Row } from 'reactstrap';
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { clone } from 'lodash';
import JournalDetailedFilter from "./JournalDetailedFilter";
import {
  reducer,
  useEffectToDispatch,
  fetchApiData,
  fetchApiDataExport,
  initialState,
  Sorting
} from "@/shared/apiTableHelper"
import { getFirstDay,getReportName } from '@/shared/helpers'
import { StickyContainer, Sticky } from "react-sticky";

const queryClient = new QueryClient()

// Accessors whose export column key differs from the CSV header key returned by the backend
const ACCESSOR_TO_EXPORT_KEY = { shownId: 'account_holder_id' };

// Backend-only export fields that aren't rendered as a screen column but still count toward "is this row empty"
const EXPORT_ONLY_NUMERIC_KEYS = ['discount_rebate_credited_to_program', 'expiration_rebate_credited_to_program'];

const NUMERIC_FIELDS = JOURNAL_DETAILED_COLUMNS
  .flatMap((group) => group.columns)
  .map((column) => column.accessor)
  .filter((accessor) => accessor && accessor !== 'name' && accessor !== 'shownId');

const EXPORT_NUMERIC_KEYS = [
  ...NUMERIC_FIELDS.map((field) => ACCESSOR_TO_EXPORT_KEY[field] || field),
  ...EXPORT_ONLY_NUMERIC_KEYS
];

const sumField = (row, field) => {
  let total = Number(row.original?.[field]) || 0;
  if (row.subRows && row.subRows.length > 0) {
    total += row.subRows.reduce((sum, subRow) => sum + sumField(subRow, field), 0);
  }
  return total;
};

const isRowEmpty = (row) => NUMERIC_FIELDS.every((field) => !sumField(row, field));

const isExportRowEmpty = (row) => EXPORT_NUMERIC_KEYS.every((key) => {
  const value = row[key];
  return value === null || value === undefined || value === '' || Number(value) === 0;
});

const DataTable = ({ organization, programs }) => {
  const defaultFrom = getFirstDay();
  const [filter, setFilter] = useState({ programs: programs, awardLevels: [], from: defaultFrom, to: new Date() });
  const [useFilter, setUseFilter] = useState(false);
  const [trigger, setTrigger] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [exportToCsv, setExportToCsv] = useState(false);
  const exportLink = React.createRef();
  const [isExporting, setIsExporting] = useState(false);
  const [hideEmptyRows, setHideEmptyRows] = useState(false);

  useEffect(() => {
    if (exportToCsv) {
      if (exportLink.current) {
        setExportToCsv(false);
        exportLink.current.link.click();
      }
    }
  }, [exportLink])

  const getVisibleExportKeys = () => allColumns
    .filter((column) => !column.columns && column.id !== 'expander' && !hiddenColumns.includes(column.id))
    .map((column) => ACCESSOR_TO_EXPORT_KEY[column.id] || column.id);

  const download = async (filterValues) => {
    try {
      setIsExporting(true);
      let tmpFilter = clone(filterValues);
      if (programs && tmpFilter.programs.length === programs.length) {
        tmpFilter.programs = ['all'];
      }
      tmpFilter.exportToCsv = 1;
      tmpFilter.limit = pageSize;
      tmpFilter.page = pageIndex + 1;

      const response = await fetchApiDataExport({
        url: apiUrl,
        filter: tmpFilter,
        sortby: queryPageSortBy,
        trigger: queryTrigger,
        visibleColumns: getVisibleExportKeys()
      });

      const results = hideEmptyRows
        ? (response.results || []).filter((row) => !isExportRowEmpty(row))
        : response.results;

      setExportData(results);
      setExportHeaders(response.headers);
      setExportToCsv(true);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  let program_columns = [
    ...JOURNAL_DETAILED_COLUMNS,
  ]
  let columns = useMemo(() => program_columns, [])

  const defaultColumn = React.useMemo(
    () => ({
      maxWidth: 100,
    }),
    []
  )

  const [{ queryPageIndex, queryPageSize, totalCount, queryPageFilter, queryPageSortBy, queryTrigger }, dispatch] =
    React.useReducer(reducer, initialState);

  const apiUrl = `/organization/${organization.id}/report/journal-detailed`;
  // When every program is selected, send the sentinel "all" instead of enumerating
  // each account_holder_id. The explicit list is ~29KB once URL-encoded, which
  // exceeds the web server's request-line limit and fails before reaching the API.
  // This must apply to EVERY request, not just the first (see ICOV3-1737) — the
  // export path below already does the same.
  const apiFilter = useMemo(() => {
    const f = clone(filter);

    if (
      programs &&
      Array.isArray(filter.programs) &&
      filter.programs.length === programs.length
    ) {
      f.programs = ["all"];
    }
    return f;
  }, [filter, programs]);

  const { isLoading, error, data, isSuccess, isFetched, isFetching } = useQuery(
    ['journal-detailed', apiUrl, queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy, queryTrigger],
    () => fetchApiData(
      {
        url: apiUrl,
        page: queryPageIndex,
        size: queryPageSize,
        filter:apiFilter,
        sortby: queryPageSortBy,
        trigger: queryTrigger
      }
    ),
    {
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  const totalPageCount = Math.ceil(totalCount / queryPageSize)

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    allColumns,
    toggleAllRowsExpanded,
    isAllRowsExpanded,
    state: { pageIndex, pageSize, sortBy, hiddenColumns }
  } = useTable({
    columns,
    data: data ? data.results : [],
    initialState: {
      pageIndex: queryPageIndex,
      pageSize: queryPageSize,
      sortBy: queryPageSortBy,
    },
    manualPagination: true, // Tell the usePagination
    pageCount: data ? totalPageCount : null,
    autoResetSortBy: false,
    autoResetExpanded: false,
    autoResetPage: false,
    disableResizing: true
  },
    useSortBy,
    useExpanded,
    usePagination,
  );

  const manualPageSize = []
  useEffectToDispatch(dispatch, { pageIndex, pageSize, gotoPage, sortBy, filter, data, useFilter, trigger });

  if (error) {
    return <p>Error: {JSON.stringify(error)}</p>;
  }

  if (isLoading || !organization?.id) {
    return <p>Loading...</p>;
  }


  if (isSuccess)
    return (
      <StickyContainer>
        <div className='table react-table report-table'>
          <div className="action-panel">
            <Row className="mx-0">
              <Col>
                <JournalDetailedFilter
                  filter={filter}
                  setFilter={setFilter}
                  setUseFilter={setUseFilter}
                  exportData={exportData}
                  exportLink={exportLink}
                  exportHeaders={exportHeaders}
                  download={download}
                  isExporting={isExporting}
                  reportName={getReportName("JournalDetailed")}
                  allColumns={allColumns}
                  hideEmptyRows={hideEmptyRows}
                  setHideEmptyRows={setHideEmptyRows}
                  toggleAllRowsExpanded={toggleAllRowsExpanded}
                  isAllRowsExpanded={isAllRowsExpanded}
                  config={{
                    keyword: false,
                    dateRange: true,
                    // awardLevels: availableAwardLevels,
                    programs: true,
                    exportToCsv: true
                  }}
                  loading={isLoading || isFetching}
                />
              </Col>
            </Row>
            <div style={{ clear: 'both' }}>&nbsp;</div>
          </div>
          {
            (isLoading || isFetching) && <p className="text-center">Loading...</p>
          }
          {
            // ref={r => { csvLinkTable = r; }}
            isSuccess &&
            <table {...getTableProps()} style={{ background: '#fff', tableLayout: 'auto' }} className="table table--bordered">
              <thead style={{ top: '60px', zIndex: '2', position: 'sticky', background: '#f9fafb' }}>
                {headerGroups.map((headerGroup, i) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={i} style={{ borderBottom: '2px solid #e5e7eb' }}>
                    {headerGroup.headers.map((column, j) => (
                      <th {...column.getHeaderProps(column.getSortByToggleProps())} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', ...(j > 0 ? { borderLeft: '1px solid #e5e7eb' } : {}) }}>
                        {column.render('Header')}
                        {column.isSorted ? <Sorting column={column} /> : ''}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="table table--bordered" {...getTableBodyProps()}>
                {(hideEmptyRows ? page?.filter((row) => !isRowEmpty(row)) : page)?.map((row,i) => {
                  prepareRow(row);
                  return (
                    <React.Fragment key={i}>
                      {row.original.label && <tr className="totalRow"><td colSpan={headerGroups[0].headers.length} style={{ fontWeight: "bold", color: "#646777", padding: '10px 12px' }}>{row.original.label}</td></tr>}
                      <tr {...row.getRowProps({ className: row.isExpanded && row.original.dinamicDepth === 1 ? ' totalRow' : '' })} >
                        {
                          row.cells.map((cell, j) => {
                            return <td {...cell.getCellProps({
                              className: row.isExpanded && row.original.dinamicDepth === 1 ? cell.column.className + ' totalRow' : cell.column.className,
                              style: {
                                textAlign: 'left',
                                ...(j > 0 ? { borderLeft: '1px solid #e5e7eb' } : {}),
                                padding: '8px 12px',
                              }
                            })} >
                              {cell.render('Cell')}
                            </td>
                          })
                        }
                      </tr>
                    </React.Fragment>
                  )
                })}
              </tbody>
              <tfoot>
                {footerGroups.map((footerGroup) => (
                  <tr {...footerGroup.getFooterGroupProps()}>
                    {footerGroup.headers.map((column, j) => (
                      <th {...column.getFooterProps()} style={{ padding: '10px 12px', textAlign: 'left', ...(j > 0 ? { borderLeft: '1px solid #e5e7eb' } : {}), borderTop: '2px solid #e5e7eb', fontWeight: 600 }}>{column.render('Footer')}</th>
                    ))}
                  </tr>
                ))}
              </tfoot>
            </table>
          }
        </div>
      </StickyContainer>
    )
}

const TableWrapper = ({ organization, programs }) => {
  if (!organization || !programs) return 'Loading...'
  return (
    <QueryClientProvider client={queryClient}>
      <DataTable organization={organization} programs={programs} />
    </QueryClientProvider>
  )
}

export default withRouter(connect((state) => ({
  organization: state.organization
}))(TableWrapper));